import type { MouseEventHandler } from 'react';
import { Box, UnstyledButton } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';

/**
 * The arrow affordance (Figma G8): a filled Accent circle with a white arrow, closing every card
 * and row in the file (`301:58186`, `426:7806`). One visual, three roles — button, link, decorative
 * mark — so the shape is defined once (`[data-ui-arrow]` in `primitive-styles.tsx`) and the roles
 * differ only in their element. The router-linked role lives in the portal (`ArrowButtonLink`),
 * which renders {@link arrowSurfaceProps} on its own anchor.
 *
 * States: hover darkens to the Accent hover step (for the mark, when the link or button that wraps
 * it is hovered), focus-visible draws the nav pills' ring, disabled drops to a grey disc. Dark and
 * blue schemes keep the Accent fill — it is the brand colour and reads on navy.
 */

/** @desc Figma draws one size, 36px; `sm` (28px) is for dense rows such as table cells. */
const DIAMETER = { sm: 28, md: 36 } as const;

const ICON_SIZE = { sm: 16, md: 20 } as const;

export type ArrowSize = keyof typeof DIAMETER;

/**
 * @desc The props that make an element the arrow: the `data-ui-arrow` hook the shared stylesheet
 * styles, and its diameter. Spread them on a `Box` (or a Mantine component) of any element.
 */
export const arrowSurfaceProps = (size: ArrowSize) =>
  ({ 'data-ui-arrow': true, w: DIAMETER[size], h: DIAMETER[size] }) as const;

/** @desc The arrow glyph alone, sized for the circle it sits in. */
export const ArrowGlyph = ({ size }: { size: ArrowSize }) => (
  <IconArrowRight size={ICON_SIZE[size]} stroke={2} aria-hidden />
);

export type ArrowButtonProps = {
  /** @desc The accessible name — the arrow itself says nothing ("Open Entain", "Next page"). */
  label: string;
  size?: ArrowSize;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  /** @desc Force the hover state on, for the style preview. */
  'data-preview-state'?: 'hover';
};

/**
 * @desc Figma's arrow affordance as a button: a 36px filled Accent circle with a white →. Use it
 * for an action; for a destination use the portal's `ArrowButtonLink`, and inside a card that is
 * itself the link use {@link ArrowMark}.
 */
export function ArrowButton({
  label,
  size = 'md',
  disabled,
  onClick,
  className,
  'data-preview-state': previewState,
}: ArrowButtonProps) {
  return (
    <UnstyledButton
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={className}
      data-preview-state={previewState}
      {...arrowSurfaceProps(size)}
    >
      <ArrowGlyph size={size} />
    </UnstyledButton>
  );
}

export type ArrowMarkProps = { size?: ArrowSize; className?: string };

/**
 * @desc The same arrow as a decoration (`aria-hidden`), for a card or row that is already one
 * link — a nested control would be invalid, and the mark takes its hover from the wrapping link.
 */
export function ArrowMark({ size = 'md', className }: ArrowMarkProps) {
  return (
    <Box component="span" aria-hidden className={className} {...arrowSurfaceProps(size)}>
      <ArrowGlyph size={size} />
    </Box>
  );
}
